import React, { useState, useEffect } from 'react';
import { X, Send } from 'lucide-react';
import useFlowStore from '../store/flowStore';
import whatsappService from '../services/whatsappService';
import toast from 'react-hot-toast';

interface NodeConfigModalProps {
  nodeId: string;
  nodeData: any;
  onClose: () => void;
}

const NodeConfigModal: React.FC<NodeConfigModalProps> = ({ nodeId, nodeData, onClose }) => {
  const updateNode = useFlowStore((state) => state.updateNode);
  const [config, setConfig] = useState(nodeData.config || {});

  const handleSave = () => {
    updateNode(nodeId, { config });
    onClose();
  };

  const renderConfigFields = () => {
    const nodeType = nodeData.triggerType || nodeData.actionType || nodeData.conditionType || nodeData.filterType;

    switch (nodeType) {
      // Trigger configurations
      case 'message_received':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pattern Messaggio (opzionale)
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                value={config.messagePattern || ''}
                onChange={(e) => setConfig({ ...config, messagePattern: e.target.value })}
                placeholder="es. /start"
              />
            </div>
          </div>
        );

      case 'keyword':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parola Chiave *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                value={config.keyword || ''}
                onChange={(e) => setConfig({ ...config, keyword: e.target.value })}
                placeholder="es. info, aiuto, prezzi"
                required
              />
            </div>
          </div>
        );

      case 'schedule':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Espressione Cron *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                value={config.schedule || ''}
                onChange={(e) => setConfig({ ...config, schedule: e.target.value })}
                placeholder="es. 0 9 * * * (ogni giorno alle 9)"
              />
            </div>
          </div>
        );

      case 'group_message':
      case 'group_join':
      case 'group_leave':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Gruppo (opzionale)
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
                value={config.groupName || ''}
                onChange={(e) => setConfig({ ...config, groupName: e.target.value })}
                placeholder="es. Assistenza Clienti"
              />
              <p className="text-xs text-gray-500 mt-1">
                Lascia vuoto per applicare a tutti i gruppi
              </p>
            </div>
          </div>
        );

      // Action configurations
      case 'send_message':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Messaggio *
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                rows={4}
                value={config.message || ''}
                onChange={(e) => setConfig({ ...config, message: e.target.value })}
                placeholder="Inserisci il messaggio da inviare..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Puoi usare variabili: {'{nome}'}, {'{numero}'}, {'{data}'}
              </p>
            </div>
          </div>
        );

      case 'send_to_group':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Gruppo *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                value={config.groupName || ''}
                onChange={(e) => setConfig({ ...config, groupName: e.target.value })}
                placeholder="Nome del gruppo WhatsApp"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ID Gruppo (opzionale)
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                value={config.groupId || ''}
                onChange={(e) => setConfig({ ...config, groupId: e.target.value })}
                placeholder="Es: 120363405331449840@g.us"
              />
              <p className="text-xs text-gray-500 mt-1">
                Se non specificato, verrà cercato per nome
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Messaggio *
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                rows={4}
                value={config.message || ''}
                onChange={(e) => setConfig({ ...config, message: e.target.value })}
                placeholder="Inserisci il messaggio da inviare al gruppo..."
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Variabili: {'{{sender}}'}, {'{{message}}'}, {'{{date}}'}, {'{{time}}'}
              </p>
            </div>
          </div>
        );

      case 'forward_media':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destinazione *
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                value={config.forwardTo || 'group'}
                onChange={(e) => setConfig({ ...config, forwardTo: e.target.value })}
              >
                <option value="group">📢 Inoltra a un Gruppo</option>
                <option value="contact">👤 Inoltra a un Contatto</option>
              </select>
            </div>

            {config.forwardTo === 'contact' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numero di Telefono *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                  value={config.contactNumber || ''}
                  onChange={(e) => setConfig({ ...config, contactNumber: e.target.value })}
                  placeholder="Es: 393755292401"
                  required
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Gruppo *
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                    value={config.groupName || ''}
                    onChange={(e) => setConfig({ ...config, groupName: e.target.value })}
                    placeholder="Nome del gruppo WhatsApp"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ID Gruppo (opzionale)
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                    value={config.groupId || ''}
                    onChange={(e) => setConfig({ ...config, groupId: e.target.value })}
                    placeholder="Es: 120363405331449840@g.us"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Se non specificato, verrà cercato per nome
                  </p>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Messaggio aggiuntivo
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                rows={2}
                value={config.caption || ''}
                onChange={(e) => setConfig({ ...config, caption: e.target.value })}
                placeholder="Testo da inviare insieme al media inoltrato..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Variabili: {'{{sender}}'}, {'{{message}}'}, {'{{date}}'}, {'{{time}}'}
              </p>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                📤 Questo nodo inoltra il media (foto, video, documento, audio) ricevuto nel messaggio originale alla destinazione scelta.
              </p>
            </div>
          </div>
        );

      case 'send_media':
        const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];
          if (!file) return;
          
          const formData = new FormData();
          formData.append('file', file);
          
          try {
            const response = await fetch('/api/upload', {
              method: 'POST',
              body: formData
            });
            
            if (!response.ok) {
              const text = await response.text();
              console.error('Upload failed:', response.status, text);
              alert('Errore upload: ' + response.status + ' - ' + text.substring(0, 100));
              return;
            }
            
            const data = await response.json();
            if (data.success) {
              setConfig({ 
                ...config, 
                mediaUrl: data.file.url,
                fileName: data.file.originalName
              });
            } else {
              alert('Errore durante l\'upload: ' + data.error);
            }
          } catch (error: any) {
            console.error('Upload error:', error);
            alert('Errore durante l\'upload del file: ' + (error.message || error));
          }
        };

        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo di Contenuto *
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                value={config.mediaType || 'link'}
                onChange={(e) => setConfig({ ...config, mediaType: e.target.value })}
              >
                <option value="link">🔗 Link (YouTube, siti web, ecc.)</option>
                <option value="image">🖼️ Immagine (JPG, PNG, GIF)</option>
                <option value="video">🎬 Video file (MP4)</option>
                <option value="audio">🎵 Audio (MP3, OGG)</option>
                <option value="document">📄 Documento (PDF, DOC, ecc.)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {config.mediaType === 'link' || !config.mediaType ? 
                  '💡 Per link YouTube: WhatsApp mostrerà automaticamente l\'anteprima del video' :
                  '💡 Puoi caricare un file o inserire un URL'
                }
              </p>
            </div>
            
            {config.mediaType !== 'link' && config.mediaType && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📤 Carica File
                </label>
                <input
                  type="file"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue bg-white"
                  onChange={handleFileUpload}
                  accept={
                    config.mediaType === 'image' ? 'image/*' :
                    config.mediaType === 'video' ? 'video/*' :
                    config.mediaType === 'audio' ? 'audio/*' :
                    config.mediaType === 'document' ? '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt' :
                    '*/*'
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  Oppure inserisci un URL qui sotto
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {config.mediaType === 'link' || !config.mediaType ? 'URL / Link *' : 'URL del File'}
              </label>
              <input
                type="url"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                value={config.mediaUrl || ''}
                onChange={(e) => setConfig({ ...config, mediaUrl: e.target.value })}
                placeholder={config.mediaType === 'link' || !config.mediaType ? 
                  'https://www.youtube.com/watch?v=...' : 
                  'https://esempio.com/file.jpg'
                }
                required={config.mediaType === 'link' || !config.mediaType}
              />
              {config.mediaUrl && (
                <p className="text-xs text-green-600 mt-1">
                  ✅ File: {config.mediaUrl.split('/').pop()}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Messaggio / Didascalia
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                rows={2}
                value={config.mediaCaption || ''}
                onChange={(e) => setConfig({ ...config, mediaCaption: e.target.value })}
                placeholder="Testo da mostrare insieme al contenuto..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Variabili: {'{{sender}}'}, {'{{message}}'}, {'{{date}}'}, {'{{time}}'}
              </p>
            </div>

            {config.mediaType === 'document' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome File
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                  value={config.fileName || ''}
                  onChange={(e) => setConfig({ ...config, fileName: e.target.value })}
                  placeholder="documento.pdf"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Nome con cui verrà mostrato il file al destinatario.
                </p>
              </div>
            )}
          </div>
        );

      case 'wait':
        return (
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durata *
                </label>
                <input
                  type="number"
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                  value={config.waitTime || 1}
                  onChange={(e) => setConfig({ ...config, waitTime: parseInt(e.target.value) })}
                  required
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unità
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                  value={config.waitUnit || 'seconds'}
                  onChange={(e) => setConfig({ ...config, waitUnit: e.target.value })}
                >
                  <option value="seconds">Secondi</option>
                  <option value="minutes">Minuti</option>
                  <option value="hours">Ore</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 'add_to_group':
      case 'remove_from_group':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ID o Nome Gruppo *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                value={config.groupId || ''}
                onChange={(e) => setConfig({ ...config, groupId: e.target.value })}
                placeholder="es. 123456789@g.us o Nome Gruppo"
                required
              />
            </div>
          </div>
        );

      case 'send_to_group':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Gruppo *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                value={config.groupName || ''}
                onChange={(e) => setConfig({ ...config, groupName: e.target.value })}
                placeholder="es. Assistenza Clienti"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Messaggio *
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                rows={3}
                value={config.message || ''}
                onChange={(e) => setConfig({ ...config, message: e.target.value })}
                placeholder="Messaggio da inviare al gruppo..."
                required
              />
            </div>
          </div>
        );

      case 'create_group':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Gruppo *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                value={config.groupName || ''}
                onChange={(e) => setConfig({ ...config, groupName: e.target.value })}
                placeholder="es. Team Marketing"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrizione (opzionale)
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                value={config.groupDescription || ''}
                onChange={(e) => setConfig({ ...config, groupDescription: e.target.value })}
                placeholder="Descrizione del gruppo"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Partecipanti (numeri separati da virgola)
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                rows={2}
                value={config.participants?.join(', ') || ''}
                onChange={(e) => setConfig({ ...config, participants: e.target.value.split(',').map(p => p.trim()).filter(p => p) })}
                placeholder="+39123456789, +39987654321"
              />
            </div>
          </div>
        );

      case 'set_group_admin':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numero Utente *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-blue"
                value={config.userId || ''}
                onChange={(e) => setConfig({ ...config, userId: e.target.value })}
                placeholder="+39123456789"
                required
              />
            </div>
          </div>
        );

      // Condition configurations
      case 'contains':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo di Condizione *
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                value={config.containsType || 'words'}
                onChange={(e) => setConfig({ ...config, containsType: e.target.value })}
              >
                <option value="words">📝 Contiene Parole</option>
                <option value="image">🖼️ Contiene Immagine</option>
                <option value="video">🎬 Contiene Video</option>
                <option value="audio">🎵 Contiene Audio</option>
                <option value="document">📄 Contiene File/Documento</option>
                <option value="any_media">📎 Contiene qualsiasi Media</option>
              </select>
            </div>

            {(config.containsType === 'words' || !config.containsType) && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Parole da Cercare *
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    rows={3}
                    value={config.value || ''}
                    onChange={(e) => setConfig({ ...config, value: e.target.value })}
                    placeholder="Inserisci le parole separate da virgola&#10;Es: parola1, parola2, parola3"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Separa più parole con la virgola. La condizione sarà vera se il messaggio contiene almeno una delle parole.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                      checked={config.ignoreCase !== false}
                      onChange={(e) => setConfig({ ...config, ignoreCase: e.target.checked })}
                    />
                    <span className="text-sm text-gray-700">Ignora maiuscole/minuscole</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                      checked={config.matchAll === true}
                      onChange={(e) => setConfig({ ...config, matchAll: e.target.checked })}
                    />
                    <span className="text-sm text-gray-700">Deve contenere TUTTE le parole (AND)</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                      checked={config.wholeWord === true}
                      onChange={(e) => setConfig({ ...config, wholeWord: e.target.checked })}
                    />
                    <span className="text-sm text-gray-700">Solo parole intere (non parti di parole)</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Campo da controllare
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    value={config.field || 'message'}
                    onChange={(e) => setConfig({ ...config, field: e.target.value })}
                  >
                    <option value="message">Testo del messaggio</option>
                    <option value="sender">Nome mittente</option>
                    <option value="phone">Numero di telefono</option>
                  </select>
                </div>
              </>
            )}

            {config.containsType && config.containsType !== 'words' && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
                <p className="text-sm text-orange-800">
                  {config.containsType === 'image' && '🖼️ La condizione sarà vera se il messaggio contiene un\'immagine'}
                  {config.containsType === 'video' && '🎬 La condizione sarà vera se il messaggio contiene un video'}
                  {config.containsType === 'audio' && '🎵 La condizione sarà vera se il messaggio contiene un audio o nota vocale'}
                  {config.containsType === 'document' && '📄 La condizione sarà vera se il messaggio contiene un file o documento'}
                  {config.containsType === 'any_media' && '📎 La condizione sarà vera se il messaggio contiene qualsiasi tipo di media'}
                </p>
              </div>
            )}
          </div>
        );

      case 'equals':
      case 'starts_with':
      case 'ends_with':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valore da Cercare *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                value={config.value || ''}
                onChange={(e) => setConfig({ ...config, value: e.target.value })}
                placeholder="Inserisci il valore"
                required
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                checked={config.ignoreCase !== false}
                onChange={(e) => setConfig({ ...config, ignoreCase: e.target.checked })}
              />
              <span className="text-sm text-gray-700">Ignora maiuscole/minuscole</span>
            </label>
          </div>
        );

      case 'time_based':
        return (
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ora Inizio
                </label>
                <input
                  type="time"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={config.timeStart || ''}
                  onChange={(e) => setConfig({ ...config, timeStart: e.target.value })}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ora Fine
                </label>
                <input
                  type="time"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={config.timeEnd || ''}
                  onChange={(e) => setConfig({ ...config, timeEnd: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Giorni della Settimana
              </label>
              <div className="flex gap-2 flex-wrap">
                {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((day, index) => (
                  <label key={day} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={(config.dayOfWeek || []).includes(index.toString())}
                      onChange={(e) => {
                        const days = config.dayOfWeek || [];
                        if (e.target.checked) {
                          setConfig({ ...config, dayOfWeek: [...days, index.toString()] });
                        } else {
                          setConfig({ ...config, dayOfWeek: days.filter((d: string) => d !== index.toString()) });
                        }
                      }}
                    />
                    <span className="text-sm">{day}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 'is_group':
        return (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              Questa condizione verifica se il messaggio proviene da un gruppo.
              Non richiede configurazioni aggiuntive.
            </div>
          </div>
        );

      case 'is_group_admin':
        return (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              Verifica se l'utente è amministratore del gruppo.
              Funziona solo nei contesti di gruppo.
            </div>
          </div>
        );

      case 'group_size':
        return (
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dimensione Minima
                </label>
                <input
                  type="number"
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={config.minSize || ''}
                  onChange={(e) => setConfig({ ...config, minSize: parseInt(e.target.value) || undefined })}
                  placeholder="es. 5"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dimensione Massima
                </label>
                <input
                  type="number"
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={config.maxSize || ''}
                  onChange={(e) => setConfig({ ...config, maxSize: parseInt(e.target.value) || undefined })}
                  placeholder="es. 100"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Lascia vuoto uno dei campi per verificare solo min o max
            </p>
          </div>
        );

      // Filter configurations
      case 'user_type':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo Utente
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={config.userType || 'new'}
                onChange={(e) => setConfig({ ...config, userType: e.target.value })}
              >
                <option value="new">Nuovo</option>
                <option value="existing">Esistente</option>
                <option value="vip">VIP</option>
              </select>
            </div>
          </div>
        );

      case 'message_type':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo Messaggio
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={config.messageType || 'text'}
                onChange={(e) => setConfig({ ...config, messageType: e.target.value })}
              >
                <option value="text">Testo</option>
                <option value="image">Immagine</option>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
                <option value="document">Documento</option>
              </select>
            </div>
          </div>
        );

      default:
        return <div>Configurazione non disponibile per questo nodo</div>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Configura {nodeData.label}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4">
          {renderConfigFields()}
        </div>
        
        <div className="flex justify-end gap-2 p-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Annulla
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-white bg-whatsapp-green hover:bg-whatsapp-dark rounded-md transition-colors"
          >
            Salva
          </button>
        </div>
      </div>
    </div>
  );
};

export default NodeConfigModal;
